// react
import { createSlice } from '@reduxjs/toolkit';
import careCircleApi from 'store/api/careCircle';
import iamApi from 'store/api/iam';
import { CareCircle, CareCircleMember, Role } from '@karehero/models';
import api from 'store/api';
import { AppDispatch } from 'store';
import { createDebouncedRefetcher } from './debouncedRefetcher';

export interface CareCircleState {
  currentCareCircle: CareCircle | null;
  currentCareCircleMember: CareCircleMember | null;
  accountCareCircles: CareCircle[] | null;
  eligibilitiesRefetching: boolean;
}

export const careCircleSlice = createSlice({
  name: 'careCircle',
  initialState: {
    currentCareCircle: null,
    currentCareCircleMember: null,
    accountCareCircles: null,
    eligibilitiesRefetching: false,
  } as CareCircleState,
  reducers: {
    setCareCircle: (state, { payload }) => {
      state.currentCareCircle = payload;
    },
    setCareCircleMember: (state, { payload }) => {
      state.currentCareCircleMember = payload;
    },
    setEligibilitiesRefetching: (state, { payload }) => {
      state.eligibilitiesRefetching = payload;
    },
    clearCareCircle: (state) => {
      state.currentCareCircle = null;
      state.currentCareCircleMember = null;
      state.accountCareCircles = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        careCircleApi.endpoints.getByAccount.matchFulfilled,
        (state, { payload }) => {
          if (!payload) return;

          state.accountCareCircles = payload;

          if (payload.length == 0) {
            return;
          }

          if (state.currentCareCircle == null) {
            const careCircle = payload[0];
            console.log(
              '[careCircleSlice] setting current care circle to care circle with ID',
              careCircle.id,
            );

            state.currentCareCircle = careCircle;
          } else {
            const careCircle = payload.find(
              (c) => c.id === state.currentCareCircle?.id,
            );

            if (careCircle) {
              state.currentCareCircle = careCircle;
            }
          }

          const primary = state.currentCareCircle.careCircleMembers.find((m) =>
            m.roles.some((r) => r.name === 'Primary Caregiver'),
          );
          if (primary) {
            console.log(
              '[careCircleSlice] setting current care circle member to primary caregiver',
              primary.account.id,
            );

            state.currentCareCircleMember = primary;
          } else {
            console.log(
              '[careCircleSlice] no primary caregiver found, setting current care circle member to first member',
              state.currentCareCircle.careCircleMembers[0].account.id,
            );

            state.currentCareCircleMember =
              state.currentCareCircle.careCircleMembers[0];
          }
        },
      )
      .addMatcher(
        careCircleApi.endpoints.getByAccount.matchRejected,
        (state) => {
          state.currentCareCircle = null;
          state.accountCareCircles = null;
        },
      )
      .addMatcher(
        careCircleApi.endpoints.getCareCircle.matchFulfilled,
        (state, { payload }) => {
          if (!payload) return;
          if (
            state.currentCareCircle &&
            state.currentCareCircle.id !== payload.id
          ) {
            console.log(
              'Not switching care circle to ID as does not match current care circle in state:',
              payload.id,
            );
            return;
          }

          console.log('Updating current care circle using ID:', payload.id);
          state.currentCareCircle = payload;

          const primary = payload.careCircleMembers.find((m) =>
            m.roles.some((r) => r.name === 'Primary Caregiver'),
          );
          if (primary) {
            state.currentCareCircleMember = primary;
          } else {
            state.currentCareCircleMember = payload.careCircleMembers[0];
          }
        },
      )
      .addMatcher(
        careCircleApi.endpoints.getCareCircle.matchRejected,
        (state) => {
          state.currentCareCircle = null;
          state.currentCareCircleMember = null;
        },
      )
      .addMatcher(iamApi.endpoints.signOut.matchFulfilled, (state) => {
        state.currentCareCircle = null;
        state.currentCareCircleMember = null;
        state.accountCareCircles = null;
      });
  },
});

export const switchCareCircle =
  (careCircleId: string) => async (dispatch: AppDispatch) => {
    const result = await dispatch(
      careCircleApi.endpoints.getCareCircle.initiate(careCircleId),
    );
    if (result.data) {
      dispatch(
        careCircleSlice.actions.setCareCircle(result.data as CareCircle),
      );
      dispatch(
        careCircleSlice.actions.setCareCircleMember(
          result.data.careCircleMembers.find((m: CareCircleMember) =>
            m.roles.some((r: Role) => r.name === 'Primary Caregiver'),
          ) as CareCircleMember,
        ),
      );
    }
  };

/**
 * Refetches eligibilities for a care circle when triggered by websocket events.
 * Debounces requests with a 300ms initial delay and enforces rate limiting.
 * Handles race conditions by queuing invalidations when one is already in progress.
 */
const eligibilitiesRefetcher = createDebouncedRefetcher<AppDispatch>(
  async (dispatch) => {
    if (!dispatch) return;
    dispatch(api.util.invalidateTags(['Eligibilities']));
  },
  {
    debounceMs: 300,
    minIntervalMs: 500,
    onRefetchingStart: (dispatch) => {
      if (!dispatch)
        throw new Error(
          '[eligibilitiesRefetcher] Cannot set eligibilities refetching state without dispatch',
        );
      dispatch(careCircleSlice.actions.setEligibilitiesRefetching(true));
    },
    onRefetchingEnd: (dispatch) => {
      if (!dispatch)
        throw new Error(
          '[eligibilitiesRefetcher] Cannot set eligibilities refetching state without dispatch',
        );
      dispatch(careCircleSlice.actions.setEligibilitiesRefetching(false));
    },
  },
);

export const refetchEligibilities =
  (careCircleID: string) => async (dispatch: AppDispatch) => {
    if (!careCircleID) return;

    await eligibilitiesRefetcher(dispatch);
  };

export const { setCareCircle, setCareCircleMember, clearCareCircle } =
  careCircleSlice.actions;

export const getCurrentCareCircle = (state: any) =>
  state.careCircle.currentCareCircle;
export const getCurrentCareCircleMember = (state: any) =>
  state.careCircle.currentCareCircleMember;
export const getAccountCareCircles = (state: any) =>
  state.careCircle.accountCareCircles;
export const getEligibilitiesRefetching = (state: any) =>
  state.careCircle.eligibilitiesRefetching;

export default careCircleSlice.reducer;
